import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocaleContext } from '../context/LocaleContext';
import { MessageSquare, Sparkles, Bot, GraduationCap, ArrowRight } from 'lucide-react';
import type { Locale } from '../types/locale';

interface Card {
  id: string;
  icon: React.ReactNode;
  title: Record<Locale, string>;
  description: Record<Locale, string>;
  url: Record<Locale, string>;
}

export const ResourceCards: React.FC = () => {
  const { locale } = useLocaleContext();
  const navigate = useNavigate();

  const cards: Card[] = [
    {
      id: 'chatgpt',
      icon: <MessageSquare className="w-6 h-6" />,
      title: {
        en: 'ChatGPT Watermarks',
        de: 'ChatGPT-Wasserzeichen',
        'pt-BR': 'Marcas d\'Água ChatGPT',
        'ms-MY': 'Tanda Air ChatGPT',
        es: 'Marcas de Agua ChatGPT',
        fr: 'Filigranes ChatGPT',
        ja: 'ChatGPT透かし',
        id: 'Watermark ChatGPT'
      },
      description: {
        en: 'What invisible characters appear in ChatGPT text and how to remove them.',
        de: 'Welche unsichtbaren Zeichen in ChatGPT-Texten auftauchen und wie Sie sie entfernen.',
        'pt-BR': 'Quais caracteres invisíveis aparecem no texto do ChatGPT e como removê-los.',
        'ms-MY': 'Aksara tidak kelihatan yang muncul dalam teks ChatGPT dan cara membuangnya.',
        es: 'Qué caracteres invisibles aparecen en el texto de ChatGPT y cómo eliminarlos.',
        fr: 'Quels caractères invisibles apparaissent dans le texte de ChatGPT et comment les supprimer.',
        ja: 'ChatGPTのテキストに現れる不可視文字とその削除方法。',
        id: 'Karakter tak terlihat apa saja yang muncul dalam teks ChatGPT dan cara menghapusnya.'
      },
      url: {
        en: '/remove-chatgpt-watermarks',
        de: '/de/chatgpt-wasserzeichen-entfernen',
        'pt-BR': '/pt-br/remover-marca-dagua-chatgpt',
        'ms-MY': '/my/buang-tanda-air-chatgpt',
        es: '/es/remover-marca-agua-chatgpt',
        fr: '/fr/supprimer-filigrane-chatgpt',
        ja: '/ja/chatgpt-watermark-remover',
        id: '/id/hapus-watermark-chatgpt'
      }
    },
    {
      id: 'gemini',
      icon: <Sparkles className="w-6 h-6" />,
      title: {
        en: 'Gemini Watermarks',
        de: 'Gemini-Wasserzeichen',
        'pt-BR': 'Marcas d\'Água Gemini',
        'ms-MY': 'Tanda Air Gemini',
        es: 'Marcas de Agua Gemini',
        fr: 'Filigranes Gemini',
        ja: 'Gemini透かし',
        id: 'Watermark Gemini'
      },
      description: {
        en: 'What\'s really in Gemini text (including SynthID) and what this tool can clean.',
        de: 'Was wirklich in Gemini-Texten steckt (einschließlich SynthID) und was dieses Tool bereinigen kann.',
        'pt-BR': 'O que realmente há no texto do Gemini (incluindo o SynthID) e o que esta ferramenta pode limpar.',
        'ms-MY': 'Apa yang sebenarnya ada dalam teks Gemini (termasuk SynthID) dan apa yang alat ini boleh bersihkan.',
        es: 'Qué hay realmente en el texto de Gemini (incluido SynthID) y qué puede limpiar esta herramienta.',
        fr: 'Ce qui se trouve vraiment dans le texte de Gemini (y compris SynthID) et ce que cet outil peut nettoyer.',
        ja: 'Geminiのテキストに実際に含まれるもの（SynthIDを含む）と、このツールで除去できるもの。',
        id: 'Apa yang sebenarnya ada dalam teks Gemini (termasuk SynthID) dan apa yang bisa dibersihkan alat ini.'
      },
      url: {
        en: '/remove-gemini-watermarks',
        de: '/de/gemini-wasserzeichen-entfernen',
        'pt-BR': '/pt-br/remover-marca-dagua-gemini',
        'ms-MY': '/my/buang-tanda-air-gemini',
        es: '/es/remover-marca-agua-gemini',
        fr: '/fr/supprimer-filigrane-gemini',
        ja: '/ja/gemini-watermark-remover',
        id: '/id/hapus-watermark-gemini'
      }
    },
    {
      id: 'claude',
      icon: <Bot className="w-6 h-6" />,
      title: {
        en: 'Claude Watermarks',
        de: 'Claude-Wasserzeichen',
        'pt-BR': 'Marcas d\'Água Claude',
        'ms-MY': 'Tanda Air Claude',
        es: 'Marcas de Agua Claude',
        fr: 'Filigranes Claude',
        ja: 'Claude透かし',
        id: 'Watermark Claude'
      },
      description: {
        en: 'How Claude\'s watermarking actually works, what this tool can and cannot remove, and how to clean invisible characters from Claude output.',
        de: 'Wie Claudes Wasserzeichen tatsächlich funktioniert, was dieses Tool entfernen kann und was nicht, und wie Sie unsichtbare Zeichen aus Claude-Ausgaben entfernen.',
        'pt-BR': 'Como a marca d\'água do Claude realmente funciona, o que esta ferramenta pode e não pode remover, e como limpar caracteres invisíveis do texto do Claude.',
        'ms-MY': 'Cara sebenar tanda air Claude berfungsi, apa yang alat ini boleh dan tidak boleh buang, dan cara membersihkan aksara tidak kelihatan daripada output Claude.',
        es: 'Cómo funciona realmente la marca de agua de Claude, qué puede y no puede eliminar esta herramienta, y cómo limpiar caracteres invisibles del texto de Claude.',
        fr: 'Comment fonctionne réellement le filigrane de Claude, ce que cet outil peut et ne peut pas supprimer, et comment nettoyer les caractères invisibles du texte de Claude.',
        ja: 'Claudeの透かしが実際にどう機能するか、このツールで削除できるものとできないもの、そしてClaudeの出力から不可視文字を除去する方法。',
        id: 'Cara kerja watermark Claude yang sebenarnya, apa yang bisa dan tidak bisa dihapus alat ini, dan cara membersihkan karakter tak terlihat dari output Claude.'
      },
      url: {
        en: '/remove-claude-watermarks',
        de: '/de/claude-wasserzeichen-entfernen',
        'pt-BR': '/pt-br/remover-marca-dagua-claude',
        'ms-MY': '/my/buang-tanda-air-claude',
        es: '/es/eliminar-marca-de-agua-claude',
        fr: '/fr/supprimer-filigrane-claude',
        ja: '/ja/claude-watermark-remover',
        id: '/id/hapus-watermark-claude'
      }
    },
    {
      id: 'students',
      icon: <GraduationCap className="w-6 h-6" />,
      title: {
        en: 'For Students',
        de: 'Für Studierende',
        'pt-BR': 'Para Estudantes',
        'ms-MY': 'Untuk Pelajar',
        es: 'Para Estudiantes',
        fr: 'Pour Étudiants',
        ja: '学生向け',
        id: 'Untuk Pelajar'
      },
      description: {
        en: 'Remove invisible characters that corrupt essay formatting, break citations, and cause copy-paste glitches in Word and Google Docs, plus guidance on using AI in line with your institution\'s policy.',
        de: 'Entfernen Sie unsichtbare Zeichen, die die Formatierung von Aufsätzen beschädigen, Zitate zerstören und Copy-and-paste-Fehler in Word und Google Docs verursachen – mit Hinweisen zur richtlinienkonformen Nutzung von KI an Ihrer Hochschule.',
        'pt-BR': 'Remova caracteres invisíveis que corrompem a formatação de redações, quebram citações e causam falhas de copiar e colar no Word e no Google Docs, além de orientações para usar IA de acordo com a política da sua instituição.',
        'ms-MY': 'Buang aksara halimunan yang merosakkan format esei, memecahkan petikan, dan menyebabkan ralat salin-tampal dalam Word dan Google Docs, serta panduan menggunakan AI selaras dengan dasar institusi anda.',
        es: 'Elimina caracteres invisibles que dañan el formato de los ensayos, rompen las citas y provocan fallos al copiar y pegar en Word y Google Docs, además de orientación para usar la IA conforme a la política de tu institución.',
        fr: 'Supprimez les caractères invisibles qui altèrent la mise en forme des dissertations, cassent les citations et provoquent des bugs de copier-coller dans Word et Google Docs, avec des conseils pour utiliser l\'IA en conformité avec la politique de votre établissement.',
        ja: 'レポートの書式を崩し、引用を壊し、WordやGoogleドキュメントでのコピー＆ペーストの不具合を引き起こす不可視文字を削除します。所属機関のポリシーに沿ってAIを使うためのガイダンスも提供します。',
        id: 'Hapus karakter tak terlihat yang merusak format esai, mengganggu sitasi, dan menyebabkan gangguan salin-tempel di Word dan Google Docs, serta panduan menggunakan AI sesuai kebijakan institusi Anda.'
      },
      url: {
        en: '/ai-watermark-remover-students',
        de: '/de/ki-wasserzeichen-entferner-studenten',
        'pt-BR': '/pt-br/removedor-ai-estudantes',
        'ms-MY': '/my/penyingkir-tanda-air-ai-pelajar',
        es: '/es/removedor-marcas-agua-estudiantes',
        fr: '/fr/suppresseur-filigrane-ia-etudiants',
        ja: '/ja/ai-watermark-remover-students',
        id: '/id/penghapus-watermark-ai-pelajar'
      }
    }
  ];

  // Section header translations
  const sectionTitle: Record<Locale, string> = {
    en: 'Specialized Solutions for Every AI Model',
    de: 'Spezifische Lösungen für jedes AI-Modell',
    'pt-BR': 'Soluções Específicas para Cada Modelo de IA',
    'ms-MY': 'Penyelesaian Khusus untuk Setiap Model AI',
    es: 'Soluciones Especializadas para Cada Modelo de IA',
    fr: 'Solutions Spécialisées pour Chaque Modèle d\'IA',
    ja: '各AIモデル専用ソリューション',
    id: 'Solusi Khusus untuk Setiap Model AI'
  };

  const sectionDescription: Record<Locale, string> = {
    en: 'Tailored guides and tools for ChatGPT, Gemini, Claude, and more.',
    de: 'Maßgeschneiderte Anleitungen und Tools für ChatGPT, Gemini, Claude und mehr.',
    'pt-BR': 'Guias e ferramentas personalizados para ChatGPT, Gemini, Claude e muito mais.',
    'ms-MY': 'Panduan dan alat yang disesuaikan untuk ChatGPT, Gemini, Claude dan banyak lagi.',
    es: 'Guías y herramientas personalizadas para ChatGPT, Gemini, Claude y más.',
    fr: 'Guides et outils sur mesure pour ChatGPT, Gemini, Claude et plus encore.',
    ja: 'ChatGPT、Gemini、Claudeなど向けのカスタマイズされたガイドとツール。',
    id: 'Panduan dan alat yang disesuaikan untuk ChatGPT, Gemini, Claude, dan lainnya.'
  };

  // CTA text patterns
  const getCtaText = (cardTitle: string): string => {
    switch (locale) {
      case 'de': return `${cardTitle} entdecken`;
      case 'pt-BR': return `Explorar ${cardTitle}`;
      case 'ms-MY': return `Terokai ${cardTitle}`;
      case 'es': return `Explorar ${cardTitle}`;
      case 'fr': return `Découvrir ${cardTitle}`;
      case 'ja': return `${cardTitle}を見る`;
      case 'id': return `Jelajahi ${cardTitle}`;
      default: return `Explore ${cardTitle}`;
    }
  };

  return (
    <div className="mt-20 mb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal-900 dark:text-white mb-4">
            {sectionTitle[locale] || sectionTitle.en}
          </h2>
          <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto">
            {sectionDescription[locale] || sectionDescription.en}
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((card) => (
            <div
              key={card.id}
              onClick={() => navigate(card.url[locale] || card.url.en)}
              className="group relative p-6 bg-gradient-to-br from-white to-neutral-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border-2 border-neutral-200 dark:border-gray-700 hover:border-accent-300 dark:hover:border-accent-600 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer overflow-hidden"
            >
              {/* Background gradient decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-accent-400/10 to-purple-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-12 h-12 bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {card.icon}
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-charcoal-900 dark:text-white mb-2">
                  {card.title[locale] || card.title.en}
                </h3>

                {/* Description */}
                <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4 line-clamp-3">
                  {card.description[locale] || card.description.en}
                </p>

                {/* CTA - Unique anchor text per card for SEO */}
                <div className="inline-flex items-center gap-1 text-accent-600 dark:text-accent-400 font-semibold text-sm group-hover:gap-2 transition-all">
                  {getCtaText(card.title[locale] || card.title.en)}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResourceCards;
